<script setup>
import { onMounted, onUnmounted, watch } from 'vue'
import {
  faExclamationCircle,
  faCheckCircle,
  faTimesCircle,
  faInfoCircle,
  faTimes
} from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'

const props = defineProps({
  /** visible toggle (parent-controlled) */
  visible: { type: Boolean, default: false },
  /** automatically hide after N ms (0 = never) */
  duration: { type: Number, default: 5000 },
  /** message text */
  message: { type: String, required: true },
  /** 'warning' | 'success' | 'error' | 'info' */
  type: { type: String, default: 'warning' }
})

const emit = defineEmits(['update:visible', 'close'])

let timer

watch(
  () => props.visible,
  (val) => {
    if (!val) return
    clearTimeout(timer)
    if (props.duration > 0) {
      timer = setTimeout(close, props.duration)
    }
  },
  { immediate: true }
)

function close () {
  clearTimeout(timer)
  emit('update:visible', false)
  emit('close')
}

onUnmounted(() => clearTimeout(timer))

const colorMap = {
  warning: { base: '#ffdb9b', accent: '#ffa502', icon: faExclamationCircle },
  success: { base: '#d4edda', accent: '#28a745', icon: faCheckCircle },
  error:   { base: '#f8d7da', accent: '#dc3545', icon: faTimesCircle },
  info:    { base: '#d1ecf1', accent: '#17a2b8', icon: faInfoCircle }
}
</script>

<template>
  <!-- v-show keeps node in DOM so animation can play both directions -->
  <div
    v-show="visible"
    :style="{
      '--base': colorMap[type].base,
      '--accent': colorMap[type].accent
    }"
    class="alert"
    :class="[ visible ? 'show' : 'hide' ]"
    role="alert"
  >
    <FontAwesomeIcon :icon="colorMap[type].icon" class="icon" />
    <span class="msg">{{ message }}</span>

    <button class="close-btn" @click="close">
      <FontAwesomeIcon :icon="faTimes" />
    </button>
  </div>
</template>

<style scoped>
/* ≈ original CodingNepal CSS, but palette driven by CSS vars */
.alert {
  position: fixed;
  top: 18px;
  right: 18px;
  padding: 20px 40px;
  min-width: 320px;
  border-left: 8px solid var(--accent);
  border-radius: 4px;
  background: var(--base);
  overflow: hidden;
  pointer-events: none;
  opacity: 0;
  z-index: 9999;
}

.alert.show { animation: slide-in 0.8s forwards; pointer-events:auto; }
.alert.hide { animation: slide-out 0.8s forwards; }

@keyframes slide-in {
  0%   { transform: translateX(100%); }
  60%  { transform: translateX(-12px); }
  80%  { transform: translateX(0); }
  100% { transform: translateX(-8px); opacity:1; }
}
@keyframes slide-out {
  0%   { transform: translateX(-8px); opacity:1; }
  20%  { transform: translateX(0); }
  100% { transform: translateX(100%); opacity:0; }
}

/* inner bits */
.icon  { position:absolute; left:20px; top:50%; transform:translateY(-50%); font-size:26px; color:var(--accent); }
.msg   { padding-left:46px; padding-right:64px; font-size:16px; color:var(--accent); user-select:none; }
.close-btn {
  position:absolute; right:0; top:50%; transform:translateY(-50%);
  background: var(--accent); background: color-mix(in srgb, var(--accent) 30%, white);
  border:none; outline:none; padding:18px 16px; cursor:pointer; transition:background .2s;
}
.close-btn:hover { background: color-mix(in srgb, var(--accent) 45%, white); }
.close-btn :deep(svg) { color:var(--accent); font-size:20px; }
</style>